import { SideMenuExtension, SuggestionMenu } from "@blocknote/core/extensions";
import {
  DragHandleMenu,
  type SideMenuProps,
  useBlockNoteEditor,
  useComponentsContext,
  useDictionary,
  useExtension,
  useExtensionState,
} from "@blocknote/react";
import { GripVertical, Plus } from "lucide-react";
import { useCallback } from "react";

export function CompactSideMenu(props: SideMenuProps) {
  const Components = useComponentsContext()!;
  const dict = useDictionary();

  const editor = useBlockNoteEditor<any, any, any>();
  const suggestionMenu = useExtension(SuggestionMenu);
  const sideMenu = useExtension(SideMenuExtension);

  const block = useExtensionState(SideMenuExtension, {
    editor,
    selector: (state) => state?.block,
  });

  const handleAddBlock = useCallback(() => {
    if (!block) return;

    const blockContent = block.content;
    const isBlockEmpty =
      blockContent !== undefined &&
      Array.isArray(blockContent) &&
      blockContent.length === 0;

    if (isBlockEmpty) {
      editor.setTextCursorPosition(block);
      suggestionMenu.openSuggestionMenu("/");
      return;
    }

    const insertedBlock = editor.insertBlocks(
      [{ type: "paragraph" }],
      block,
      "after",
    )[0];
    editor.setTextCursorPosition(insertedBlock);
    suggestionMenu.openSuggestionMenu("/");
  }, [block, editor, suggestionMenu]);

  if (!block) return null;

  const DragMenuComponent = props.dragHandleMenu || DragHandleMenu;

  return (
    <Components.SideMenu.Root className="bn-side-menu">
      <Components.SideMenu.Button
        className="bn-button"
        label={dict.side_menu.add_block_label}
        onClick={handleAddBlock}
        icon={<Plus size={12} data-test="dragHandleAdd" />}
      />

      <Components.Generic.Menu.Root
        onOpenChange={(open: boolean) => {
          if (open) {
            sideMenu.freezeMenu();
          } else {
            sideMenu.unfreezeMenu();
          }
        }}
        position="left"
      >
        <Components.Generic.Menu.Trigger>
          <Components.SideMenu.Button
            className="bn-button"
            label={dict.side_menu.drag_handle_label}
            draggable={true}
            onDragStart={(event) => sideMenu.blockDragStart(event, block)}
            onDragEnd={sideMenu.blockDragEnd}
            icon={<GripVertical size={12} data-test="dragHandle" />}
          />
        </Components.Generic.Menu.Trigger>
        <DragMenuComponent />
      </Components.Generic.Menu.Root>
    </Components.SideMenu.Root>
  );
}
